import * as React from "react";
import copy from "copy-to-clipboard";

import { DateLike } from "~/lib/date";
import LegacyDispatcher from "~/lib/legacyDispatcher";
import JobLogLineStore from "~/stores/JobLogLineStore";
import TestTooltip from "~/components/analytics/shared/Tooltips/TestTooltip";
import { track } from "~/lib/segmentAnalytics";
import {
  LogCopyPermalink,
  LogLine,
  LogLineContent,
  LogLineNumber,
  LogTimestamp,
} from "~/components/shared/Log";

interface Props extends React.HTMLAttributes<HTMLTableRowElement> {
  id: string;
  number: number;
  line: string;
  startedAt: DateLike | null | undefined;
  timestampsOn: boolean | null | undefined;
  timestampsInUTC: boolean | null | undefined;
  onTimestampClick: (arg1: React.MouseEvent<HTMLElement>) => void | null | undefined;
  innerRef?: React.Ref<HTMLTableRowElement> | null;
}

// When changing to Typescript, this can be imported from 'TestTooltip'
type TestProps = {
  uuid: string;
  activity: {
    activities: Array<{
      createdAt: string;
      duration: number;
      url: string;
      humanDuration: string;
      id: string;
      result: string;
    }>;
    limit: number;
  };
  reliability: string;
  failedCount: number;
  p50Duration: number;
  p95Duration: number;
  url: string;
};

type State = {
  highlighted: boolean;
  test?: TestProps;
  testLoading: boolean;
  showTooltip: boolean;
};

const getStateFromStores = (id: string) => ({
  highlighted: JobLogLineStore.isHighlighted(id),
});

export default class JobLogOutputLine extends React.PureComponent<Props, State> {
  tooltipWrapperNode: HTMLSpanElement | null | undefined;

  constructor(props: Props) {
    super(props);

    this.state = {
      ...getStateFromStores(props.id),
      test: undefined,
      testLoading: false,
      showTooltip: false,
    };
  }

  componentDidMount() {
    JobLogLineStore.addChangeListener(this.props.id, this.handleLineStoreChange);

    if (!Features.BuildSidebar) {
      this.emitHighlighted();
    }
  }

  componentWillUnmount() {
    JobLogLineStore.removeChangeListener(this.props.id, this.handleLineStoreChange);
  }

  componentDidUpdate() {
    if (!Features.BuildSidebar) {
      this.emitHighlighted();
    }
  }

  // Notify the stores that a highlighted line has been rendered. This gives the
  // stores an opportunity to scroll to this line if they need to.
  emitHighlighted() {
    if (!Features.RefactorLogScrollBehaviour && this.state.highlighted) {
      // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
      return LegacyDispatcher.emit("job_log_line:highlighted", {
        lineId: this.props.id,
        el: this.props.innerRef,
      });
    }
  }

  handleLineStoreChange = () => {
    return this.setState(getStateFromStores(this.props.id));
  };

  handleClick = (evt: React.MouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    return LegacyDispatcher.emit("job_log_line:highlight", {
      lineId: this.props.id,
    });
  };

  handleCopyPermalink = (evt: React.MouseEvent) => {
    // Prevent the line from being unhighlighted
    evt.stopPropagation();

    const url = new URL(window.location.href);
    url.hash = this.props.id;
    copy(url.toString());
  };

  handleTimestampClick = (evt: React.MouseEvent<HTMLElement>) => {
    evt.preventDefault();

    if (this.props.onTimestampClick) {
      this.props.onTimestampClick(evt);
    }
  };

  handleTooltipLineClick = async (evt: React.MouseEvent<HTMLElement>) => {
    const { target } = evt;

    if (!(target instanceof HTMLAnchorElement) || !target.href) {
      return;
    }

    const testAnalyticsUrlRegex =
      /\/organizations\/([^/]+)\/analytics\/suites\/([^/]+)\/tests\/([^/]+)/;
    const testAnalyticsMatch = target.href.match(testAnalyticsUrlRegex);

    if (testAnalyticsMatch) {
      evt.preventDefault();
      const organizationSlug = testAnalyticsMatch[1];
      this.setState({ showTooltip: true });
      document.documentElement &&
        document.documentElement.addEventListener("click", this.handleDocumentClick);

      _trackTestTooltipClicked({
        organizationSlug: organizationSlug,
      });
    }
  };

  handleDocumentClick = (evt: MouseEvent) => {
    if (!(evt.target instanceof HTMLElement)) {
      return;
    }

    if (this.tooltipWrapperNode && !this.tooltipWrapperNode.contains(evt.target)) {
      this.setState({ showTooltip: false });
      document.documentElement &&
        document.documentElement.removeEventListener("click", this.handleDocumentClick);
    }
  };

  render() {
    const {
      timestampsInUTC,
      id,
      line,
      number,
      onTimestampClick,
      startedAt,
      timestampsOn,
      innerRef,
      ...props
    } = this.props;

    return (
      <LogLine ref={innerRef} highlighted={this.state.highlighted} {...props}>
        <LogLineNumber
          number={this.props.number}
          highlighted={this.state.highlighted}
          copyButton={<LogCopyPermalink onClick={this.handleCopyPermalink} />}
          onClick={this.handleClick}
        />

        {this.props.timestampsOn && (
          <LogTimestamp
            time={this.props.startedAt}
            utc={this.props.timestampsInUTC}
            onClick={this.handleTimestampClick}
          />
        )}

        <LogLineContent colSpan={2}>
          <span
            ref={(tooltipWrapperNode) => (this.tooltipWrapperNode = tooltipWrapperNode)}
            style={{ position: "relative" }}
          >
            <span
              dangerouslySetInnerHTML={{ __html: this.props.line }}
              onClick={this.handleTooltipLineClick}
            />
            {this.state.showTooltip && (
              <TestTooltip
                // @ts-expect-error - TS2322 - Type 'HTMLSpanElement | null | undefined' is not assignable to type 'HTMLElement'.
                wrapperNode={this.tooltipWrapperNode}
              />
            )}
          </span>
        </LogLineContent>
      </LogLine>
    );
  }
}

type TrackingProps = {
  organizationSlug: string;
};

function _trackTestTooltipClicked({ organizationSlug }: TrackingProps) {
  track("TA Test Tooltip Opened", {
    source: "Job/Log/Output/Line",
    organization_slug: organizationSlug,
  });
}
